/**
 * Filtro de privacidad de los datos de uso (ver docs-vadym/uso/README.md).
 *
 * Funciona por LISTA BLANCA: solo pasan las claves de `datos` declaradas
 * aquí y con el formato esperado; todo lo demás se descarta en silencio.
 * Así, aunque alguien pase por error un importe, un beneficiario, una nota o
 * un texto escrito, no sale del dispositivo.
 *
 * Nunca se recogen: importes o saldos, nombres de beneficiarios, cuentas o
 * categorías, notas, IBAN ni nada tecleado. Las categorías van solo por id.
 */

/** Valores tipo «palabra clave» (nombre de modal, opción, acción…). */
const RE_CLAVE = /^[a-z0-9][a-z0-9_.-]{0,39}$/i;
/** Ruta ya normalizada (ver `normalizarRuta`). */
const RE_RUTA = /^\/[a-z0-9_\-/:]{0,79}$/i;
/** Ids internos (uuid): categorías, grupos. */
const RE_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RE_MES = /^\d{4}-\d{2}$/;
/** «archivo.js:12:34», sin carpetas. */
const RE_FUENTE = /^[a-z0-9_.-]{1,60}(:\d{1,7}){0,2}$/i;

type Validador = (v: unknown) => unknown;

const clave: Validador = v =>
  typeof v === 'string' && RE_CLAVE.test(v) ? v : undefined;
const ruta: Validador = v =>
  typeof v === 'string' && RE_RUTA.test(v) ? v : undefined;
const uuid: Validador = v =>
  typeof v === 'string' && RE_UUID.test(v) ? v : undefined;
const mes: Validador = v =>
  typeof v === 'string' && RE_MES.test(v) ? v : undefined;
/** Contadores, tiempos y medidas de pantalla: NUNCA importes. */
const numero: Validador = v =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.round(v * 100) / 100
    : undefined;
const booleano: Validador = v => (typeof v === 'boolean' ? v : undefined);
const listaClaves: Validador = v =>
  Array.isArray(v)
    ? v
        .slice(0, 40)
        .map(clave)
        .filter((x): x is string => x !== undefined)
    : undefined;
const mensaje: Validador = v =>
  typeof v === 'string' ? limpiarMensajeError(v) : undefined;
const fuente: Validador = v =>
  typeof v === 'string' && RE_FUENTE.test(v) ? v : undefined;

/**
 * Claves permitidas en `datos` y cómo se valida cada una. Añadir una clave
 * aquí es la ÚNICA forma de recoger algo nuevo: revisa que no pueda llevar
 * dinero ni texto del usuario y documéntala en docs-vadym/uso/README.md.
 */
export const CLAVES_PERMITIDAS: Readonly<Record<string, Validador>> = {
  // Navegación
  pantalla: ruta,
  anterior: ruta,
  // Nombres internos (modal, opción de la brújula, acción, tipo de aviso…)
  modal: clave,
  raiz: clave,
  opcion: clave,
  opciones: listaClaves,
  accion: clave,
  origen: clave,
  tipo: clave,
  boton: clave,
  aviso: clave,
  motivo: clave,
  resultado: clave,
  // Tema de un «Avísame» (p. ej. 'banco')
  tema: clave,
  // Ids (nunca nombres)
  categoria: uuid,
  desde: uuid,
  hasta: uuid,
  // Mes del presupuesto
  mes,
  // Contadores, tiempos (ms) y medidas (px, escala)
  ms: numero,
  n: numero,
  alto: numero,
  ancho: numero,
  escala: numero,
  // Errores (mensaje sin comillas ni cifras largas; archivo:línea)
  mensaje,
  fuente,
  // Sí/no
  exito: booleano,
  conBoton: booleano,
  activo: booleano,
};

/** Devuelve solo las claves permitidas y con valor válido. */
export function limpiarDatos(
  datos: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const limpio: Record<string, unknown> = {};
  if (!datos || typeof datos !== 'object') {
    return limpio;
  }
  for (const [k, v] of Object.entries(datos)) {
    const validar = Object.prototype.hasOwnProperty.call(CLAVES_PERMITIDAS, k)
      ? CLAVES_PERMITIDAS[k]
      : undefined;
    if (!validar) {
      continue;
    }
    const valor = validar(v);
    if (valor !== undefined) {
      limpio[k] = valor;
    }
  }
  return limpio;
}

/**
 * Mensaje de error sin datos: quita lo que va entre comillas (suele ser un
 * valor), correos, IBAN y cualquier cifra de 3 o más dígitos o con decimales
 * (podría ser un importe). Máximo 160 caracteres.
 */
export function limpiarMensajeError(texto: string): string {
  return texto
    .replace(/(["'`«“‘])[^"'`»”’]*(["'`»”’])/g, '$1…$2')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<correo>')
    .replace(/\b[A-Z]{2}\d{2}[A-Z0-9 ]{10,30}\b/g, '<iban>')
    .replace(/\d+[.,]\d+/g, '<n>')
    .replace(/\d{3,}/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
}

const RE_SEGMENTO = /^[a-z][a-z0-9_-]{0,30}$/i;

/**
 * Ruta de pantalla sin ids ni parámetros: `/accounts/3f2a…` → `/accounts/:id`.
 * Cualquier trozo que no sea una palabra sencilla se sustituye por `:id`, así
 * que nunca viaja un id de cuenta, un texto de búsqueda ni la query.
 */
export function normalizarRuta(pathname: string): string {
  const sinQuery = pathname.split(/[?#]/)[0] ?? '';
  const partes = sinQuery
    .split('/')
    .filter(Boolean)
    .slice(0, 6)
    .map(p => (RE_SEGMENTO.test(p) && !/\d{3,}/.test(p) ? p : ':id'));
  return '/' + partes.join('/');
}
